import React, { useCallback } from 'react';
import dynamic from 'next/dynamic';
import { makeStyles } from 'src/makeStyles';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import groupBy from 'lodash/groupBy';
import ListText from '../ListText';
import { useTranslation } from '../../src/i18n';
import { colors, fontSize } from '../../constants/styleConstants';
import { getCastGroupByRole, getOtherColumnData, getCrewGroupByRole, groupMembersByWork } from '../../utils/common';
import { TP, EMPTY_INFO_MARK, ENSEMBLES_TYPE_SLUGS } from '../../constants/consts';
import DropdownButton from '../DropdownButton';

// dynamic imports
const CastRow = dynamic(() => import('./castRow'));
const TitledBlock = dynamic(() => import('../TitledBlock'));

const useStyles = makeStyles()(theme => ({
  container: {
    width: '100%',
    marginBottom: 10,
  },
  types: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  teamItem: {
    display: 'flex',
    flexGrow: 1,
    flexBasis: 0,
  },
  teamItemBlock: {
    width: '100%',
    paddingTop: '2px',
  },
  title: {
    display: 'flex',
    fontWeight: 'bold',
    justifyContent: 'center',
    marginBottom: '4px',
  },
  mobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  toggleButton: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      background: 0,
      boxShadow: 'none',
      border: 0,
      padding: '15px 0',
    },
    '& p': {
      fontSize: fontSize.smallTitle,
      color: colors.grey3,
      margin: 0,
      textAlign: 'center',
    },
  },
  expandIcon: {
    float: 'left',
    color: colors.blueChip,
  },
  line: {
    opacity: '0.2',
    backgroundColor: '#979797',
    marginLeft: '-20px',
    width: '115%',
  },
  bold: {
    fontWeight: 'bold',
  },
  castDivision: {
    color: colors.grey2,
    marginBottom: '2px',
    lineHeight: 0.7,
    display: 'flex',
    justifyContent: 'center',
  },
  worksDropdown: {
    display: 'flex',
    justifyContent: 'center',
    margin: '7px 0',
  },
  empty: {
    display: 'flex',
    justifyContent: 'center',
  },
  ensembleTitle: {
    textAlign: 'center',
    fontWeight: 'bold',
    margin: '10px 0 0 5px',
  },
  ensembleBlock: {
    display: 'flex',
  },
  ensembleKey: {
    color: 'rgb(58, 58, 58)',
    width: '50%',
    margin: '5px 6px',
    display: 'flex',
    alignSelf: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'right',
    fontWeight: 'bold',
  },
  ensembleValue: {
    color: 'rgb(58, 58, 58)',
    width: '50%',
    margin: '5px 6px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'left',
    wordBreak: 'break-word',
  },
}));

const PerformanceCastContent = ({ production, showEmpty, showText }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [groupedCast, setGroupedCast] = React.useState(() => {
    if (production) {
      const groupedWorks = groupMembersByWork(production);

      return {
        workMembers: groupedWorks,
        selectedWork: groupedWorks[0],
      };
    }

    return {
      workMembers: [],
      selectedWork: {},
    };
  });

  const { workMembers, selectedWork } = groupedCast;

  const setWorkMembers = useCallback(value => {
    setGroupedCast(state => ({ ...state, workMembers: value }));
  }, []);

  const selectWork = useCallback(value => {
    setGroupedCast(state => ({ ...state, selectedWork: value }));
  }, []);

  React.useEffect(() => {
    const groupedWorks = groupMembersByWork(production);
    setGroupedCast({
      workMembers: groupedWorks,
      selectedWork: groupedWorks[0],
    });
  }, [production, selectWork, setWorkMembers]);

  const filterCastWithWorkRole = selectedWork?.cast?.filter(
    prof => prof?.workRole?.name || prof?.workRole?.original_name,
  );
  const castGroupByRole = groupBy(
    filterCastWithWorkRole,
    item => item?.workRole?.original_name || item?.workRole?.name,
  );
  const otherGroupByRole = groupBy(selectedWork?.other, 'workRoleReference');
  const filterCrewWithProfession = selectedWork?.crew?.filter(prof => prof?.profession || prof?.professionReference);
  const crewGroupByRole = groupBy(
    filterCrewWithProfession?.map(member => ({
      ...member,
      prof: member?.profession?.name || member?.professionReference,
    })),
    'prof',
  );

  const crewColumnData = getCrewGroupByRole(crewGroupByRole);
  const castColumnData = getCastGroupByRole(castGroupByRole);

  const otherColumnData = getOtherColumnData(otherGroupByRole);
  const castData = selectedWork?.cast && selectedWork?.cast.length ? selectedWork?.cast : [];
  const castColumn = Object.keys(castColumnData).length > 0 ? castColumnData : otherColumnData;

  // eslint-disable-next-line no-restricted-syntax
  for (const [key] of Object.entries(crewColumnData)) {
    if (key === 'Chorus' || key === 'Orchestra') {
      delete crewColumnData[key];
    }
  }

  const ensemblesInfo = () => {
    const formattedData = production?.ensembles?.map(({ id, ensemble }) => ({
      label: ensemble?.name,
      value: ensemble?.name,
      id: ensemble?.id,
      parentId: id,
      slug: ensemble?.ensembleType?.slug,
    }));
    const types = Object.values(ENSEMBLES_TYPE_SLUGS);
    return types.reduce((acc, field) => {
      const fieldData = formattedData?.filter(({ slug }) => slug === field);
      if (fieldData) {
        acc[field] = fieldData;
      }
      return acc;
    }, {});
  };

  const ensembles = ensemblesInfo();

  return (
    <div className={classes.container}>
      {workMembers?.length > 0 && (
        <>
          <p className={classes.title}>{t(`${TP}.CAST_CREW`)}</p>
          <div className={classes.worksDropdown}>
            {workMembers?.length === 1 ? (
              <div>{workMembers[0]?.label}</div>
            ) : (
              <DropdownButton
                onChange={value => selectWork(value)}
                options={workMembers}
                selectedValue={selectedWork}
              />
            )}
          </div>
          <TitledBlock className={classes.teamItemBlock}>
            {isEmpty(crewColumnData) ? (
              <p className={classes.empty}>{EMPTY_INFO_MARK}</p>
            ) : (
              <>
                <CastRow
                  type="crew"
                  castGroupByRole={crewColumnData}
                  short
                  showText={showText}
                  className={classes.bold}
                />
              </>
            )}
          </TitledBlock>
        </>
      )}
      {(castData?.length > 0 || showEmpty) && (
        <TitledBlock className={classes.teamItemBlock}>
          {isEmpty(castColumn) ? (
            <p className={classes.empty}>{EMPTY_INFO_MARK}</p>
          ) : (
            <>
              <span className={classes.castDivision}>~</span>
              <CastRow type="cast" castGroupByRole={castColumn} short showText={showText} />
            </>
          )}
        </TitledBlock>
      )}
      {!isEmpty(otherColumnData) && (
        <>
          <p className={classes.title}>{t(`${TP}.OTHERS`)}</p>
          <TitledBlock className={classes.teamItemBlock}>
            {isEmpty(otherColumnData) ? (
              <p className={classes.empty}>{EMPTY_INFO_MARK}</p>
            ) : (
              <CastRow type="other" castGroupByRole={otherColumnData} short showText={showText} />
            )}
          </TitledBlock>
        </>
      )}
      {production?.ensembles?.length > 0 && (
        <>
          <p className={classes.ensembleTitle}>{t(`${TP}.FN_ENSEMBLES_INFO`)}</p>
          <>
            {ensembles[ENSEMBLES_TYPE_SLUGS.ORCHESTRA]?.length > 0 && (
              <div className={classes.ensembleBlock}>
                <p className={classes.ensembleKey}>{t(`${TP}.m_FN_ORCHESTRA`)}</p>
                <div className={classes.ensembleValue}>
                  <ListText
                    noWrap
                    list={ensembles[ENSEMBLES_TYPE_SLUGS.ORCHESTRA]}
                    display="list"
                    listSeparator=" / "
                    fieldToDisplay="value"
                  />
                </div>
              </div>
            )}
            {ensembles[ENSEMBLES_TYPE_SLUGS.CHOIR]?.length > 0 && (
              <div className={classes.ensembleBlock}>
                <p className={classes.ensembleKey}>{t(`${TP}.m_FN_CHORUS`)}</p>
                <div className={classes.ensembleValue}>
                  <ListText
                    list={ensembles[ENSEMBLES_TYPE_SLUGS.CHOIR]}
                    display="list"
                    listSeparator=" / "
                    fieldToDisplay="value"
                    noWrap
                  />
                </div>
              </div>
            )}
            {ensembles[ENSEMBLES_TYPE_SLUGS.BALLET]?.length > 0 && (
              <div className={classes.ensembleBlock}>
                <p className={classes.ensembleKey}>{t(`${TP}.FN_BALLET_OR_GROUP`)}</p>
                <div className={classes.ensembleValue}>
                  <ListText
                    list={ensembles[ENSEMBLES_TYPE_SLUGS.BALLET]}
                    display="list"
                    listSeparator=" / "
                    fieldToDisplay="value"
                    noWrap
                  />
                </div>
              </div>
            )}
            {ensembles[ENSEMBLES_TYPE_SLUGS.OTHERS]?.length > 0 && (
              <div className={classes.ensembleBlock}>
                <p className={classes.ensembleKey}>{t(`${TP}.FN_CASTING_TOOL_OTHER_ROLES`)}</p>
                <div className={classes.ensembleValue}>
                  <ListText
                    listSeparator=" / "
                    list={ensembles[ENSEMBLES_TYPE_SLUGS.OTHERS]}
                    display="list"
                    fieldToDisplay="value"
                    noWrap
                  />
                </div>
              </div>
            )}
          </>
        </>
      )}
    </div>
  );
};

PerformanceCastContent.propTypes = {
  production: PropTypes.object,
};

export default PerformanceCastContent;
